
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Brand } from "@/types";
import { Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { invokeAdmin } from "@/services/functions";
import { v4 as uuidv4 } from "uuid";
import { brandSchema, useFormValidation, validateImageFile, type BrandFormData } from "@/utils/validation";
import { uploadImage, useImagePreview } from "@/utils/imageManager";
import { useStandardizedError, useStandardizedLoading } from "@/hooks/useStandardizedError";
import { AdminPagination, usePagination } from '@/components/ui/admin-pagination';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { checkBrandDependencies } from '@/utils/referentialIntegrity';

const BrandsManager = () => {
  const { toast } = useToast();
  const { handleError, handleSuccess } = useStandardizedError();
  const { setLoading, isLoading } = useStandardizedLoading();
  const { pagination, setPage, setLimit, setTotal } = usePagination(10);
  const { validate, validateField } = useFormValidation(brandSchema);
  const { clearPreviews } = useImagePreview();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    brandId: string;
    brandName: string;
  }>({
    open: false,
    brandId: '',
    brandName: ''
  });

  const [formData, setFormData] = useState<BrandFormData>({
    name: "",
    logo: "",
    description: "",
  });

  // Fetch brands on component mount and pagination changes
  useEffect(() => {
    fetchBrands();
  }, [pagination.page, pagination.limit]);

  const fetchBrands = async () => {
    setLoading('fetch', true);
    try {
      const { data, error } = await invokeAdmin('admin-manage-brands', {
        action: 'list',
        page: pagination.page,
        limit: pagination.limit
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao caregar marcas');
      }

      setBrands(data.brands || []);
      setTotal(data.total || 0);
    } catch (error) {
      handleError(error, 'BrandsManager.fetchBrands');
    } finally {
      setLoading('fetch', false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      logo: "",
      description: "",
    });
    setValidationErrors({});
    clearPreviews(); // Clean up any preview URLs
    setIsAdding(false);
    setEditingId(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingLogo(true);

      // Get current logo for cleanup
      const currentLogo = formData.logo;

      // Upload with automatic cleanup of old image
      const result = await uploadImage(
        file,
        {
          bucket: 'brand_logos',
          folder: 'brands',
          maxSize: 2 * 1024 * 1024, // 2MB limit for brand logos
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        },
        currentLogo || undefined
      );

      setFormData(prev => ({ ...prev, logo: result.url }));

      toast({
        title: "Sucesso",
        description: "Imagem carregada com sucesso",
      });
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast({
        title: "Erro no upload",
        description: error.message || "Não foi possível caregar a imagem",
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAddBrand = async () => {
    if (!formData.name) {
      toast({
        title: "Erro",
        description: "Nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    setLoading('create', true);
    try {
      const { data, error } = await invokeAdmin('admin-manage-brands', {
        action: 'create',
        brandData: {
          name: formData.name,
          logo_url: formData.logo || null,
          description: formData.description || null
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao criar marca');
      }

      // Atualizar a lista de marcas
      await fetchBrands();

      handleSuccess("Marca criada com sucesso");
      resetForm();
    } catch (error) {
      handleError(error, 'BrandsManager.handleAddBrand');
    } finally {
      setLoading('create', false);
    }
  };

  const handleEditBrand = async () => {
    if (!editingId) return;

    setLoading('update', true);
    try {
      const { data, error } = await invokeAdmin('admin-manage-brands', {
        action: 'update',
        brandId: editingId,
        brandData: {
          name: formData.name,
          logo_url: formData.logo,
          description: formData.description
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao actualizar marca');
      }

      // Atualizar a lista de marcas
      await fetchBrands();

      handleSuccess("Marca actualizada com sucesso");
      resetForm();
    } catch (error) {
      handleError(error, 'BrandsManager.handleEditBrand');
    } finally {
      setLoading('update', false);
    }
  };

  const startEditing = (brand: Brand) => {
    setFormData({
      name: brand.name,
      logo: brand.logo_url || brand.logo || "",
      description: brand.description,
    });
    setEditingId(brand.id);
    setIsAdding(true);
  };

  const openDeleteConfirmation = async (brand: Brand) => {
    setLoading('checkDependencies', true);
    try {
      const dependencyCheck = await checkBrandDependencies(brand.id);

      if (dependencyCheck.hasDependent) {
        handleError(new Error(dependencyCheck.message), 'BrandsManager.openDeleteConfirmation');
        return;
      }

      setDeleteConfirmation({
        open: true,
        brandId: brand.id,
        brandName: brand.name
      });
    } catch (error) {
      handleError(error, 'BrandsManager.openDeleteConfirmation');
    } finally {
      setLoading('checkDependencies', false);
    }
  };

  const handleDeleteBrand = async (id: string) => {
    setLoading('delete', true);
    try {
      // Se houver um logo, extrair o path do URL para excluí-lo do storage
      const brand = brands.find(b => b.id === id);
      const logoUrl = brand?.logo_url || brand?.logo;
      if (logoUrl) {
        // Extract full path including folder (brands/uuid.ext)
        const urlParts = logoUrl.split('/');
        const fileName = urlParts.pop();
        if (fileName) {
          const fullPath = `brands/${fileName}`;
          // Tentar remover a imagem do storage (não bloquear caso falhe)
          await supabase.storage
            .from('brand_logos')
            .remove([fullPath]);
        }
      }

      const { data, error } = await invokeAdmin('admin-manage-brands', {
        action: 'delete',
        brandId: id
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao eliminar marca');
      }

      // Atualizar a lista de marcas
      setBrands(prev => prev.filter(brand => brand.id !== id));

      handleSuccess("Marca eliminada com sucesso");
    } catch (error) {
      handleError(error, 'BrandsManager.handleDeleteBrand');
    } finally {
      setLoading('delete', false);
    }
  };

  const clearLogoImage = () => {
    setFormData(prev => ({ ...prev, logo: "" }));
  };

  return (
    <div>
      {isAdding ? (
        <div className="bg-brand-dark p-4 rounded-md mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? "Editar Marca" : "Adicionar Nova Marca"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm mb-1">Nome*</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="bg-brand-darker text-white"
                placeholder="Nome da marca"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Logo da Marca</label>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                  className="bg-brand-darker border border-gray-600 text-white hover:bg-gray-700 flex-shrink-0"
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Carregando...</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploadingLogo}
                />

                {formData.logo && (
                  <div className="relative h-14 w-14 flex-shrink-0">
                    <img
                      src={formData.logo}
                      alt="Logo Preview"
                      className="h-full w-full object-contain border border-gray-600 rounded bg-white p-1"
                    />
                    <button
                      type="button"
                      onClick={clearLogoImage}
                      className="absolute -top-2 -right-2 bg-brand-red rounded-full p-1 text-white"
                      title="Remover imagem"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">Descrição</label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="bg-brand-darker text-white"
                placeholder="Descreva a marca"
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={editingId ? handleEditBrand : handleAddBrand}
              className="bg-brand-red text-white hover:bg-brand-red/90"
              disabled={isLoading('create') || isLoading('update')}
            >
              {(isLoading('create') || isLoading('update')) ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>A processar...</span>
                </div>
              ) : editingId ? "Gravar Alterações" : "Adicionar Marca"}
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800"
              disabled={isLoading('create') || isLoading('update')}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={() => setIsAdding(true)}
          className="mb-6 bg-brand-red text-white hover:bg-brand-red/90"
        >
          <Plus size={16} className="mr-2" /> Adicionar Marca
        </Button>
      )}

      <div className="rounded-md border border-gray-700 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-dark hover:bg-brand-dark/80">
              <TableHead className="text-white">Logo</TableHead>
              <TableHead className="text-white">Nome</TableHead>
              <TableHead className="text-white">Descrição</TableHead>
              <TableHead className="text-white w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading('fetch') ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6 text-gray-400">
                  Carregando marcas...
                </TableCell>
              </TableRow>
            ) : brands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6 text-gray-400">
                  Nenhuma marca registada
                </TableCell>
              </TableRow>
            ) : (
              brands.map(brand => (
                <TableRow key={brand.id} className="hover:bg-brand-dark/50">
                  <TableCell>
                    {brand.logo_url || brand.logo ? (
                      <img
                        src={brand.logo_url || brand.logo}
                        alt={brand.name}
                        className="w-10 h-10 object-contain bg-white rounded p-1"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                        <img 
                          src="/lovable-uploads/3763a7cb-2c18-4d96-baaf-120c4a5b8014.png" 
                          alt="Logo padrão" 
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{brand.name}</TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    {brand.description || "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEditing(brand)}
                        disabled={isLoading('update') || isLoading('delete')}
                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-800"
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openDeleteConfirmation(brand)}
                        disabled={isLoading('delete') || isLoading('checkDependencies')}
                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-800"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {brands.length > 0 && (
        <div className="mt-4">
          <AdminPagination
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}

      <ConfirmationDialog
        open={deleteConfirmation.open}
        onOpenChange={(open) => setDeleteConfirmation(prev => ({ ...prev, open }))}
        title="Confirmar eliminação"
        description={`Tem a certeza que pretende eliminar a marca "${deleteConfirmation.brandName}"? Esta ação não pode ser desfeita.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => handleDeleteBrand(deleteConfirmation.brandId)}
        destructive={true}
      />
    </div>
  );
};

export default BrandsManager;
